/**
 * Recunoașterea materialului de chei lipit în formular: blocuri PEM, chei
 * publice OpenSSH pe o linie și, pentru certificate X.509, perioada de
 * valabilitate.
 *
 * Totul e parsare de text și ASN.1 — nicio operație criptografică: nu se
 * verifică semnături, nu se decriptează nimic, nu se validează lanțul. Rulează
 * doar în browser, pe conținutul pe care userul îl are deja în formular.
 */

export type PemKind = 'certificate' | 'certificate_request' | 'private_key' | 'public_key';

export type PemFormat = 'pem' | 'openssh_public' | 'putty';

export type PemErrorCode =
  | 'no_pem'
  | 'missing_begin'
  | 'missing_end'
  | 'label_mismatch'
  | 'unknown_label'
  | 'empty_body'
  | 'bad_base64'
  | 'bad_structure'
  | 'bad_certificate'
  | 'bad_ssh_public';

export interface X509Info {
  notBefore: Date;
  notAfter: Date;
  /** Common Name din subject; `null` dacă lipsește sau nu e un șir cunoscut. */
  subjectCn: string | null;
}

export interface PemItem {
  kind: PemKind;
  format: PemFormat;
  /** Eticheta PEM (`CERTIFICATE`, …), tipul cheii OpenSSH sau `PuTTY`. */
  label: string;
  /** Conținutul decodat din base64 (gol pentru PuTTY). */
  bytes: Uint8Array;
  /** Completat doar pentru `kind === 'certificate'`. */
  certificate?: X509Info;
}

export type PemInspection =
  | { ok: true; items: PemItem[] }
  | { ok: false; error: PemErrorCode };

/** Etichetele PEM acceptate și ce reprezintă fiecare. */
const PEM_LABELS: Record<string, PemKind> = {
  CERTIFICATE: 'certificate',
  'X509 CERTIFICATE': 'certificate',
  'TRUSTED CERTIFICATE': 'certificate',
  'CERTIFICATE REQUEST': 'certificate_request',
  'NEW CERTIFICATE REQUEST': 'certificate_request',
  'PRIVATE KEY': 'private_key',
  'ENCRYPTED PRIVATE KEY': 'private_key',
  'RSA PRIVATE KEY': 'private_key',
  'EC PRIVATE KEY': 'private_key',
  'DSA PRIVATE KEY': 'private_key',
  'OPENSSH PRIVATE KEY': 'private_key',
  'PUBLIC KEY': 'public_key',
  'RSA PUBLIC KEY': 'public_key',
};

const OPENSSH_LABEL = 'OPENSSH PRIVATE KEY';
const OPENSSH_MAGIC = 'openssh-key-v1\0';

const MARKER = /-----(BEGIN|END) ([A-Z0-9][A-Z0-9 ]*?)-----/g;
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;
const SSH_KEY_TYPE = /^(?:ssh-|ecdsa-sha2-|sk-ssh-|sk-ecdsa-)[A-Za-z0-9@.-]+$/;
const PUTTY_HEADER = /^PuTTY-User-Key-File-\d+:\s*\S+/;

// ==========================================================
// Base64
// ==========================================================

/** Decodează base64 standard (spațiile albe sunt ignorate); `null` dacă e stricat. */
export function decodeBase64(text: string): Uint8Array | null {
  const compact = text.replace(/\s+/g, '');
  if (compact.length === 0 || compact.length % 4 !== 0 || !BASE64.test(compact)) return null;
  let binary: string;
  try {
    binary = atob(compact);
  } catch {
    return null;
  }
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

// ==========================================================
// DER (ASN.1) — doar cât trebuie pentru un certificat X.509
// ==========================================================

interface Tlv {
  tag: number;
  /** Începutul conținutului. */
  start: number;
  /** Primul octet de după conținut. */
  end: number;
}

const TAG_INTEGER = 0x02;
const TAG_OID = 0x06;
const TAG_UTF8 = 0x0c;
const TAG_UTC_TIME = 0x17;
const TAG_GENERALIZED_TIME = 0x18;
const TAG_UNIVERSAL_STRING = 0x1c;
const TAG_BMP_STRING = 0x1e;
const TAG_SEQUENCE = 0x30;
const TAG_SET = 0x31;
const TAG_CONTEXT_0 = 0xa0;
/** NumericString, PrintableString, TeletexString, IA5String, VisibleString. */
const TAGS_SINGLE_BYTE_STRING = [0x12, 0x13, 0x14, 0x16, 0x1a];
/** id-at-commonName, 2.5.4.3. */
const OID_COMMON_NAME = [0x55, 0x04, 0x03];

/** Citește un element tag-lungime-valoare; `null` la orice ieșire din limite. */
function readTlv(buf: Uint8Array, offset: number, limit: number): Tlv | null {
  if (offset + 2 > limit) return null;
  const tag = buf[offset];
  // Tag-urile pe mai mulți octeți nu apar în structurile de care avem nevoie.
  if ((tag & 0x1f) === 0x1f) return null;
  let pos = offset + 1;
  let length = buf[pos++];
  if (length & 0x80) {
    const count = length & 0x7f;
    // 0 = lungime nedefinită (BER, interzisă în DER); peste 4 octeți nu e realist.
    if (count === 0 || count > 4 || pos + count > limit) return null;
    length = 0;
    for (let i = 0; i < count; i++) length = length * 256 + buf[pos++];
  }
  const end = pos + length;
  if (end > limit) return null;
  return { tag, start: pos, end };
}

function readChildren(buf: Uint8Array, parent: Tlv): Tlv[] | null {
  const out: Tlv[] = [];
  let pos = parent.start;
  while (pos < parent.end) {
    const child = readTlv(buf, pos, parent.end);
    if (!child) return null;
    out.push(child);
    pos = child.end;
  }
  return out;
}

function asciiOf(buf: Uint8Array, tlv: Tlv): string {
  let out = '';
  for (let i = tlv.start; i < tlv.end; i++) out += String.fromCharCode(buf[i]);
  return out;
}

/** Construiește data UTC și respinge valorile imposibile (luna 13, 30 februarie). */
function utcDate(y: number, mo: number, d: number, h: number, mi: number, s: number): Date | null {
  const date = new Date(Date.UTC(y, mo - 1, d, h, mi, s));
  const same =
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === mo - 1 &&
    date.getUTCDate() === d &&
    date.getUTCHours() === h &&
    date.getUTCMinutes() === mi &&
    date.getUTCSeconds() === s;
  return same ? date : null;
}

/**
 * `Time` din X.509: UTCTime (`YYMMDDHHMMSSZ`, anii 1950–2049 — RFC 5280
 * §4.1.2.5.1) sau GeneralizedTime (`YYYYMMDDHHMMSSZ`). Doar forma cu `Z`.
 */
function parseTime(buf: Uint8Array, tlv: Tlv): Date | null {
  const text = asciiOf(buf, tlv);
  if (tlv.tag === TAG_UTC_TIME) {
    const m = /^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?Z$/.exec(text);
    if (!m) return null;
    const yy = Number(m[1]);
    return utcDate(
      yy >= 50 ? 1900 + yy : 2000 + yy,
      Number(m[2]),
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      Number(m[6] ?? '0'),
    );
  }
  if (tlv.tag === TAG_GENERALIZED_TIME) {
    const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(?:\.\d+)?Z$/.exec(text);
    if (!m) return null;
    return utcDate(
      Number(m[1]),
      Number(m[2]),
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      Number(m[6]),
    );
  }
  return null;
}

function decodeString(buf: Uint8Array, tlv: Tlv): string | null {
  const bytes = buf.subarray(tlv.start, tlv.end);
  if (tlv.tag === TAG_UTF8) {
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      return null;
    }
  }
  if (TAGS_SINGLE_BYTE_STRING.includes(tlv.tag)) return asciiOf(buf, tlv);
  const width = tlv.tag === TAG_BMP_STRING ? 2 : tlv.tag === TAG_UNIVERSAL_STRING ? 4 : 0;
  if (width === 0 || bytes.length % width !== 0) return null;
  let out = '';
  for (let i = 0; i < bytes.length; i += width) {
    let code = 0;
    for (let j = 0; j < width; j++) code = code * 256 + bytes[i + j];
    if (code > 0x10ffff) return null;
    out += String.fromCodePoint(code);
  }
  return out;
}

/** Ultimul CN dintr-un `Name` (cel mai specific); `null` dacă nu există. */
function commonNameOf(buf: Uint8Array, name: Tlv): string | null {
  let found: string | null = null;
  for (const rdn of readChildren(buf, name) ?? []) {
    if (rdn.tag !== TAG_SET) continue;
    for (const pair of readChildren(buf, rdn) ?? []) {
      if (pair.tag !== TAG_SEQUENCE) continue;
      const parts = readChildren(buf, pair);
      if (!parts || parts.length !== 2 || parts[0].tag !== TAG_OID) continue;
      const oid = buf.subarray(parts[0].start, parts[0].end);
      if (oid.length !== OID_COMMON_NAME.length) continue;
      if (!OID_COMMON_NAME.every((b, i) => oid[i] === b)) continue;
      found = decodeString(buf, parts[1]) ?? found;
    }
  }
  return found;
}

/** Un singur SEQUENCE care acoperă exact tot bufferul. */
function isSingleSequence(der: Uint8Array): boolean {
  const top = readTlv(der, 0, der.length);
  return top !== null && top.tag === TAG_SEQUENCE && top.end === der.length;
}

/**
 * Extrage valabilitatea și CN-ul dintr-un certificat X.509 în DER.
 *
 *   Certificate    ::= SEQUENCE { tbsCertificate, signatureAlgorithm, signature }
 *   TBSCertificate ::= SEQUENCE { [0] version OPTIONAL, serialNumber, signature,
 *                                 issuer, validity, subject, … }
 *
 * `null` dacă structura nu e cea de mai sus. Semnătura NU e verificată.
 */
export function parseX509Certificate(der: Uint8Array): X509Info | null {
  const cert = readTlv(der, 0, der.length);
  if (!cert || cert.tag !== TAG_SEQUENCE || cert.end !== der.length) return null;
  const certParts = readChildren(der, cert);
  if (!certParts || certParts.length !== 3 || certParts[0].tag !== TAG_SEQUENCE) return null;

  const tbs = readChildren(der, certParts[0]);
  if (!tbs) return null;
  const base = tbs[0]?.tag === TAG_CONTEXT_0 ? 1 : 0;
  const [serial, signature, issuer, validity, subject] = tbs.slice(base, base + 5);
  if (!subject) return null;
  if (serial.tag !== TAG_INTEGER) return null;
  if ([signature, issuer, validity, subject].some((part) => part.tag !== TAG_SEQUENCE)) {
    return null;
  }

  const times = readChildren(der, validity);
  if (!times || times.length !== 2) return null;
  const notBefore = parseTime(der, times[0]);
  const notAfter = parseTime(der, times[1]);
  if (!notBefore || !notAfter) return null;

  return { notBefore, notAfter, subjectCn: commonNameOf(der, subject) };
}

// ==========================================================
// Chei publice OpenSSH pe o linie
// ==========================================================

/**
 * `ssh-ed25519 AAAA… comentariu`. Blob-ul începe cu numele tipului (lungime pe
 * 4 octeți + șir), deci îl putem compara cu cel declarat pe linie.
 */
function parseSshPublicLine(line: string): PemItem | null {
  const parts = line.trim().split(/\s+/);
  // Linie de `authorized_keys` cu opțiuni în față: tipul e primul cuvânt recunoscut.
  const at = parts.findIndex((word) => SSH_KEY_TYPE.test(word));
  if (at < 0 || at + 1 >= parts.length) return null;
  const type = parts[at];
  const blob = decodeBase64(parts[at + 1]);
  if (!blob || blob.length < 4) return null;
  const nameLength = ((blob[0] << 24) | (blob[1] << 16) | (blob[2] << 8) | blob[3]) >>> 0;
  if (nameLength !== type.length || blob.length <= 4 + nameLength) return null;
  for (let i = 0; i < nameLength; i++) {
    if (blob[4 + i] !== type.charCodeAt(i)) return null;
  }
  return { kind: 'public_key', format: 'openssh_public', label: type, bytes: blob };
}

function looksLikeSshPublicLine(line: string): boolean {
  return line.trim().split(/\s+/).some((word) => SSH_KEY_TYPE.test(word));
}

// ==========================================================
// Blocuri PEM
// ==========================================================

/** Scoate antetele RFC 1421 (`Proc-Type:`, `DEK-Info:`) ale cheilor criptate vechi. */
function splitHeaders(body: string): { base64: string; hasHeaders: boolean } {
  const lines = body.split('\n');
  const first = lines.findIndex((line) => line.trim() !== '');
  if (first < 0 || !/^[A-Za-z0-9-]+:/.test(lines[first].trim())) {
    return { base64: body, hasHeaders: false };
  }
  const blank = lines.findIndex((line, i) => i > first && line.trim() === '');
  return { base64: blank < 0 ? '' : lines.slice(blank + 1).join('\n'), hasHeaders: true };
}

function inspectBlock(label: string, body: string): PemItem | PemErrorCode {
  const kind = PEM_LABELS[label];
  if (!kind) return 'unknown_label';
  const { base64, hasHeaders } = splitHeaders(body);
  if (base64.trim() === '') return 'empty_body';
  const bytes = decodeBase64(base64);
  if (!bytes) return 'bad_base64';

  const item: PemItem = { kind, format: 'pem', label, bytes };
  if (label === OPENSSH_LABEL) {
    const magic = String.fromCharCode(...bytes.subarray(0, OPENSSH_MAGIC.length));
    return magic === OPENSSH_MAGIC ? item : 'bad_structure';
  }
  // Cu antete, corpul e criptat (format vechi OpenSSL) — nu mai e DER lizibil.
  if (hasHeaders) return item;
  if (kind === 'certificate') {
    const certificate = parseX509Certificate(bytes);
    return certificate ? { ...item, certificate } : 'bad_certificate';
  }
  return isSingleSequence(bytes) ? item : 'bad_structure';
}

/**
 * Analizează textul lipit într-un câmp de cheie sau certificat. Acceptă mai
 * multe blocuri la rând (lanț de certificate, certificat + cheie) și text în
 * jurul lor (ieșirea `openssl x509 -text`, „Bag Attributes”).
 */
export function inspectPem(text: string): PemInspection {
  const source = text.replace(/\r\n?/g, '\n');
  const items: PemItem[] = [];
  let open: { label: string; bodyStart: number } | null = null;
  let markers = 0;

  for (const match of source.matchAll(MARKER)) {
    markers++;
    const [whole, edge, label] = match;
    if (edge === 'BEGIN') {
      if (open) return { ok: false, error: 'missing_end' };
      open = { label, bodyStart: match.index + whole.length };
      continue;
    }
    if (!open) return { ok: false, error: 'missing_begin' };
    if (open.label !== label) return { ok: false, error: 'label_mismatch' };
    const result = inspectBlock(label, source.slice(open.bodyStart, match.index));
    if (typeof result === 'string') return { ok: false, error: result };
    items.push(result);
    open = null;
  }
  if (open) return { ok: false, error: 'missing_end' };
  if (markers > 0) return { ok: true, items };

  // Fără niciun marcaj PEM: chei publice OpenSSH (una pe linie) sau PuTTY.
  const lines = source
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '' && !line.startsWith('#'));
  if (lines.length === 0) return { ok: false, error: 'no_pem' };
  if (PUTTY_HEADER.test(lines[0])) {
    const item: PemItem = {
      kind: 'private_key',
      format: 'putty',
      label: 'PuTTY',
      bytes: new Uint8Array(0),
    };
    return { ok: true, items: [item] };
  }
  for (const line of lines) {
    const item = parseSshPublicLine(line);
    if (!item) {
      const sshLike = looksLikeSshPublicLine(line);
      return { ok: false, error: sshLike ? 'bad_ssh_public' : 'no_pem' };
    }
    items.push(item);
  }
  return { ok: true, items };
}

/** Primul certificat din text (într-un lanț, cel al entității) sau `null`. */
export function firstCertificate(inspection: PemInspection): X509Info | null {
  if (!inspection.ok) return null;
  return inspection.items.find((item) => item.certificate)?.certificate ?? null;
}

// ==========================================================
// Legătura cu formularul de credential
// ==========================================================

/** Ce ne așteptăm să conțină un câmp, după cheia atributului din schemă. */
export type PemExpectation = 'certificate' | 'private_key' | 'key' | 'any';

export type PemWarning =
  | PemErrorCode
  | 'expected_certificate'
  | 'expected_private_key'
  | 'expected_key';

/**
 * Câmpurile verificate: zonele de text din tipurile `certificate` și `ssh_key`
 * a căror cheie sugerează material PEM (`cert_pem`, `private_key`,
 * `private_key_pem` în schemele implicite). Restul — de pildă un câmp de
 * notițe adăugat de tenant — rămân neatinse (`null`).
 */
export function pemExpectationFor(
  credentialType: string,
  attr: { key: string; type: string },
): PemExpectation | null {
  if (credentialType !== 'certificate' && credentialType !== 'ssh_key') return null;
  if (attr.type !== 'textarea') return null;
  const key = attr.key.toLowerCase();
  if (key.includes('private')) return 'private_key';
  if (key.includes('key')) return 'key';
  if (key.includes('cert') || key.includes('chain')) return 'certificate';
  if (key.includes('pem')) return 'any';
  return null;
}

/** Avertismentul de afișat sub câmp; `null` când conținutul e în regulă. */
export function pemWarning(
  inspection: PemInspection,
  expectation: PemExpectation,
): PemWarning | null {
  if (!inspection.ok) return inspection.error;
  const kinds = inspection.items.map((item) => item.kind);
  if (expectation === 'certificate' && !kinds.includes('certificate')) {
    return 'expected_certificate';
  }
  if (expectation === 'private_key' && !kinds.includes('private_key')) {
    return 'expected_private_key';
  }
  if (
    expectation === 'key' &&
    !kinds.includes('private_key') &&
    !kinds.includes('public_key')
  ) {
    return 'expected_key';
  }
  return null;
}
